import React from 'react';
import { motion } from 'framer-motion';

interface MetricItem {
  label: string;
  value: number;
  color: string;
  icon?: React.ReactNode;
}

interface MetricComparisonProps {
  title: string;
  metrics: MetricItem[];
  maxValue?: number;
}

const MetricComparison: React.FC<MetricComparisonProps> = ({
  title,
  metrics,
  maxValue,
}) => {
  const max = maxValue || Math.max(...metrics.map(m => m.value));

  return (
    <motion.div
      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-lg font-bold text-gray-900 mb-6">{title}</h3>

      <div className="space-y-4">
        {metrics.map((metric, index) => {
          const percentage = (metric.value / max) * 100;

          return (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {/* Label Row */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {metric.icon && (
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${metric.color}20`, color: metric.color }}
                    >
                      {metric.icon}
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-700">
                    {metric.label}
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-900">
                  {metric.value.toLocaleString()}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{
                    backgroundColor: metric.color,
                    boxShadow: `0 0 10px ${metric.color}40`,
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 1, ease: 'easeOut' }}
                />

                {/* Shimmer Effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                  initial={{ x: '-100%' }}
                  animate={{ x: '200%' }}
                  transition={{
                    delay: index * 0.1 + 1,
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 3,
                  }}
                />
              </div>

              {/* Percentage Label */}
              <motion.div
                className="text-xs text-gray-500 mt-1 text-right"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.5 }}
              >
                {percentage.toFixed(1)}%
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default MetricComparison;

